export class ApiError extends Error {
    status;
    detail;
    constructor(status, detail) {
        super(detail.message);
        this.status = status;
        this.detail = detail;
        this.name = "ApiError";
    }
}
const mutations = new Set(["DELETE", "PATCH", "POST", "PUT"]);
export class ApiClient {
    options;
    cache = new Map();
    csrfToken;
    fetchImplementation;
    getDeviceId;
    workspaceId;
    workspaceVersion = 0;
    constructor(options) {
        this.options = options;
        this.fetchImplementation =
            options.fetch ?? globalThis.fetch.bind(globalThis);
        this.getDeviceId = options.getDeviceId ?? createBrowserDeviceId;
    }
    selectWorkspace(workspaceId) {
        if (workspaceId === this.workspaceId)
            return;
        this.workspaceId = workspaceId;
        this.workspaceVersion += 1;
        this.cache.clear();
    }
    setSession(session) {
        this.csrfToken = session?.csrfToken;
        if (!session)
            this.selectWorkspace(undefined);
    }
    async getSession() {
        const session = await this.request("/auth/session");
        this.setSession(session);
        return session;
    }
    async request(path, init = {}, options = {}) {
        const method = (init.method ?? "GET").toUpperCase();
        const cacheKey = `${this.workspaceId ?? "global"}:${method}:${path}`;
        if (method === "GET" && options.cache && this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }
        const selectedWorkspaceVersion = this.workspaceVersion;
        const headers = new Headers(init.headers);
        headers.set("accept", "application/json");
        headers.set("x-device-id", this.getDeviceId());
        headers.set("x-request-id", createRequestId());
        if (this.workspaceId)
            headers.set("x-workspace-id", this.workspaceId);
        if (mutations.has(method) && this.csrfToken) {
            headers.set("x-csrf-token", this.csrfToken);
        }
        if (init.body && !headers.has("content-type")) {
            headers.set("content-type", "application/json");
        }
        const response = await this.fetchImplementation(`${normalizeApiOrigin(this.options.baseUrl)}/api/v1${path}`, { ...init, credentials: "include", headers, method });
        const body = await readBody(response);
        if (!response.ok)
            throw toApiError(response.status, body);
        if (selectedWorkspaceVersion !== this.workspaceVersion) {
            throw new ApiError(409, {
                code: "WORKSPACE_CONTEXT_CHANGED",
                message: "工作区已切换，请重试当前操作。",
            });
        }
        const data = body.data;
        if (method === "GET" && options.cache)
            this.cache.set(cacheKey, data);
        if (mutations.has(method))
            this.cache.clear();
        return data;
    }
}
function normalizeApiOrigin(baseUrl) {
    return baseUrl.replace(/\/$/, "").replace(/\/api\/v1$/, "");
}
function createRequestId() {
    return globalThis.crypto?.randomUUID?.() ?? `req_${Date.now()}`;
}
function createBrowserDeviceId() {
    const storageKey = "yingchuan.device-id";
    try {
        const current = globalThis.localStorage?.getItem(storageKey);
        if (current)
            return current;
        const created = `device_${createRequestId().replaceAll("-", "")}`;
        globalThis.localStorage?.setItem(storageKey, created);
        return created;
    }
    catch {
        return `device_${createRequestId().replaceAll("-", "")}`;
    }
}
async function readBody(response) {
    if (response.status === 204)
        return { data: undefined };
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json"))
        return undefined;
    return response.json();
}
function toApiError(status, body) {
    const fallback = {
        code: status >= 500 ? "SERVICE_UNAVAILABLE" : "REQUEST_FAILED",
        message: status >= 500 ? "服务暂时不可用，请稍后重试。" : "请求未能完成。",
    };
    if (!body || typeof body !== "object" || !("error" in body)) {
        return new ApiError(status, fallback);
    }
    const error = body.error;
    if (!error || typeof error !== "object")
        return new ApiError(status, fallback);
    const detail = error;
    return new ApiError(status, {
        code: typeof detail.code === "string" ? detail.code : fallback.code,
        message: typeof detail.message === "string" ? detail.message : fallback.message,
        requestId: typeof detail.requestId === "string" ? detail.requestId : undefined,
    });
}
